import { CommercialType } from '@talon/web/lib/api';
import { Box, Button, ResponsiveContext, Text, TextInput } from 'grommet';
import { useContext } from 'react';
import { EmptyState } from '../../EmptyState';
import { IMediaAssetLengthMap } from '../ProjectWizard';

interface IAssetsTabProps {
  media?: CommercialType[];
  setAssetCounts: (value: IMediaAssetLengthMap) => void;
  assetCounts: IMediaAssetLengthMap;
  onMedia: () => void;
  showErrors: boolean;
}

export const validAssetLength = (length: number) => Number.isInteger(length) && length >= 1 && length <= 600;

export const AssetsTab = ({ media, setAssetCounts, assetCounts, onMedia, showErrors }: IAssetsTabProps) => {
  const mobile = useContext(ResponsiveContext) === 'small';
  const setLengths = (medium: CommercialType, lengths: number[]) =>
    setAssetCounts({ ...assetCounts, [medium]: lengths });

  return (
    <Box flex={false} pad="medium" gap="medium">
      {!media?.length ? (
        <EmptyState title="No media chosen" body="Pick what you're producing on the Media step."
          actionLabel="Go to Media" onAction={onMedia} />
      ) : media.map((medium) => {
        const lengths = assetCounts[medium] ?? [30];
        return (
          <Box key={medium} gap="small" border={{ side: 'bottom', color: '#EDEDED' }} pad={{ bottom: 'medium' }}>
            <Text size="14px" weight={600} color="#222">{medium} assets</Text>
            {lengths.map((length, index) => (
              <Box key={`${medium}-${index}`} gap="xsmall">
                <Box direction={mobile ? 'column' : 'row'} align={mobile ? 'start' : 'center'} gap="small">
                  <Text size="13px" color="#555" style={{ width: 62 }}>Asset {index + 1}</Text>
                  <Box direction="row" align="center" gap="xsmall" wrap
                    style={mobile ? { width: '100%' } : { flex: 1, minWidth: 0 }}>
                    {[15, 30, 60].map((preset) => (
                      <Button key={preset} label={String(preset)}
                        onClick={() => setLengths(medium, lengths.map((value, i) => i === index ? preset : value))}
                        style={{ border: `1px solid ${length === preset ? '#0d48a1' : '#BBB'}`, borderRadius: 20,
                          background: length === preset ? '#e8eef8' : 'white', color: length === preset ? '#0d48a1' : '#333',
                          minHeight: 40, padding: '4px 12px', fontSize: 14, fontWeight: 600 }} />
                    ))}
                    {[15, 30, 60].includes(length) ? <Button label="Other…"
                      onClick={() => setLengths(medium, lengths.map((value, i) => i === index ? NaN : value))}
                      style={{ border: '1px solid #BBB', borderRadius: 20, background: 'white',
                        minHeight: 40, padding: '4px 12px', fontSize: 14, fontWeight: 600 }} /> :
                      <Box width="56px" flex={false}>
                        <TextInput aria-label={`${medium} asset ${index + 1} length in seconds`}
                          type="number" min={1} max={600} value={Number.isNaN(length) ? '' : length}
                          onChange={(event) => setLengths(medium, lengths.map((value, i) =>
                            i === index ? (event.target.value === '' ? NaN : Number(event.target.value)) : value))}
                          style={{ width: 56, minHeight: 40, padding: '4px 6px' }} />
                      </Box>}
                    <Text size="13px" color="#777">sec</Text>
                    {lengths.length > 1 && <Button plain a11yTitle={`Remove ${medium} asset ${index + 1}`} label="×"
                      onClick={() => setLengths(medium, lengths.filter((_, i) => i !== index))}
                      style={{ marginLeft: mobile ? 0 : 'auto', minWidth: 40, minHeight: 40, color: '#777' }} />}
                  </Box>
                </Box>
                {showErrors && !validAssetLength(length) && <Text size="12px" color="#c62828">Enter a length in seconds</Text>}
              </Box>
            ))}
            <Button plain label={`+ Add another ${medium} asset`}
              onClick={() => setLengths(medium, [...lengths, 30])} alignSelf="start" color="#0d48a1" />
          </Box>
        );
      })}
    </Box>
  );
};
